import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { downloadCsv } from "../../lib/csv.js";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, Tooltip as PieTooltip,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { apiClient } from "../../lib/api/client.js";

// ── tipi locali ───────────────────────────────────────────────────────────────

interface KpiData {
  totale_rilevazioni: number;
  ok: number;
  parziali: number;
  non_conformi: number;
  percentuale_conformita: number;
  anomalie_per_causale: { causale: string; count: number }[];
  time_series: { giorno: string; totale: number; ok: number; non_conformi: number }[];
}

interface NonConformeItem {
  id: string;
  timestamp: string;
  qualita: string;
  causale: string | null;
  nota: string | null;
  barcode: string | null;
  codice_tari: string | null;
  intestatario: string | null;
  indirizzo: string | null;
}

// ── costanti ──────────────────────────────────────────────────────────────────

const CAUSALE_LABEL: Record<string, string> = {
  BIDONE_NON_ESPOSTO: "Bidone non esposto",
  CONTENUTO_NON_CONFORME: "Conferimento errato",
  SOVRARIEMPIMENTO: "Sovrariempimento",
  CONTENITORE_DANNEGGIATO: "Contenitore danneggiato",
  ACCESSO_IMPEDITO: "Accesso impedito",
  ETICHETTA_ILLEGGIBILE: "Etichetta illeggibile",
  BARCODE_NON_RICONOSCIUTO: "Barcode non riconosciuto",
  ALTRO: "Altro",
};

const PIE_COLORS = ["#16a34a", "#dc2626", "#f59e0b"];

function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function fmt(iso: string) {
  return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso));
}
function fmtTs(iso: string) {
  return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

// ── componente ────────────────────────────────────────────────────────────────

export function ReportPage() {
  const [dataDa, setDataDa] = useState(daysAgo(30));
  const [dataA, setDataA] = useState(today());
  const chartsRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [exportingEsp, setExportingEsp] = useState(false);

  const params = new URLSearchParams({ data_da: dataDa, data_a: dataA });

  const { data: kpi, isLoading: kpiLoading } = useQuery<KpiData>({
    queryKey: ["report", "kpi", dataDa, dataA],
    queryFn: () => apiClient.get(`/api/v1/reporting/kpi?${params}`),
  });

  const { data: ncData, isLoading: ncLoading } = useQuery<{ items: NonConformeItem[] }>({
    queryKey: ["report", "non-conformi", dataDa, dataA],
    queryFn: () => apiClient.get(`/api/v1/reporting/non-conformi?${params}&limit=100`),
  });

  const pieData = kpi ? [
    { name: "Conformi", value: kpi.ok },
    { name: "Non conformi", value: kpi.non_conformi },
    { name: "Con riserva", value: kpi.parziali },
  ].filter(d => d.value > 0) : [];

  // ── export CSV esposizioni per utenza ────────────────────────────────────────
  async function handleExportEsposizioni() {
    setExportingEsp(true);
    try {
      const p = new URLSearchParams({ data_da: dataDa, data_a: dataA });
      const rows = await apiClient.get<Record<string, unknown>[]>(`/api/v1/reporting/esposizioni-per-utenza?${p}`);
      downloadCsv(`esposizioni_per_utenza_${dataDa}_${dataA}.csv`, rows);
    } finally {
      setExportingEsp(false);
    }
  }

  // ── export PDF ──────────────────────────────────────────────────────────────
  async function handleExportPdf() {
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();

      // intestazione
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("TARAS — Report raccolta porta a porta", W / 2, 18, { align: "center" });
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Periodo: ${fmt(dataDa)} – ${fmt(dataA)}`, W / 2, 25, { align: "center" });
      doc.text(`Generato il ${fmt(today())}`, W / 2, 30, { align: "center" });

      // KPI
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Riepilogo KPI", 14, 42);
      autoTable(doc, {
        startY: 46,
        head: [["Totale rilevazioni", "Conformi", "Non conformi", "% Conformità"]],
        body: [[
          kpi?.totale_rilevazioni ?? 0,
          kpi?.ok ?? 0,
          kpi?.non_conformi ?? 0,
          `${kpi?.percentuale_conformita ?? 0}%`,
        ]],
        theme: "grid",
        headStyles: { fillColor: [29, 78, 216] },
      });

      // cattura grafici
      if (chartsRef.current) {
        const canvas = await html2canvas(chartsRef.current, { scale: 1.5, backgroundColor: "#fff" });
        const imgData = canvas.toDataURL("image/png");
        const imgH = (canvas.height / canvas.width) * (W - 28);
        const lastY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
        doc.addImage(imgData, "PNG", 14, lastY, W - 28, imgH);
      }

      // tabella non-conformi
      doc.addPage();
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Dettaglio non conformità", 14, 18);
      const ncRows = (ncData?.items ?? []).map(r => [
        fmtTs(r.timestamp),
        r.codice_tari ?? "—",
        r.intestatario ?? "—",
        r.indirizzo ?? "—",
        CAUSALE_LABEL[r.causale ?? ""] ?? r.causale ?? "—",
        r.nota ?? "",
      ]);
      autoTable(doc, {
        startY: 22,
        head: [["Data/ora", "Cod. TARI", "Intestatario", "Indirizzo", "Causale", "Note"]],
        body: ncRows.length ? ncRows : [["Nessun dato nel periodo selezionato", "", "", "", "", ""]],
        theme: "striped",
        headStyles: { fillColor: [220, 38, 38] },
        styles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 22 }, 4: { cellWidth: 32 } },
      });

      doc.save(`TARAS_report_${dataDa}_${dataA}.pdf`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Dal</label>
          <input
            type="date" value={dataDa} max={dataA}
            onChange={e => setDataDa(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Al</label>
          <input
            type="date" value={dataA} min={dataDa} max={today()}
            onChange={e => setDataA(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handleExportPdf}
          disabled={exporting || kpiLoading}
          className="ml-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          {exporting ? (
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          )}
          Esporta PDF
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Totale rilevazioni" value={kpi?.totale_rilevazioni} loading={kpiLoading} color="blue" />
          <KpiCard label="Conformi" value={kpi?.ok} loading={kpiLoading} color="green"
            sub={kpi ? `${kpi.percentuale_conformita}%` : undefined} />
          <KpiCard label="Non conformi" value={kpi?.non_conformi} loading={kpiLoading} color="red" />
          <KpiCard label="Con riserva" value={kpi?.parziali} loading={kpiLoading} color="amber" />
        </div>

        {/* grafici */}
        <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* bar chart andamento giornaliero */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Andamento giornaliero</h3>
            {kpiLoading ? <ChartSkeleton /> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={kpi?.time_series ?? []} margin={{ top: 0, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="giorno" tickFormatter={v => v.slice(5)} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={v => fmt(v as string)}
                    formatter={(val, name) => [val, name === "ok" ? "Conformi" : "Non conformi"]}
                  />
                  <Legend formatter={v => v === "ok" ? "Conformi" : "Non conformi"} />
                  <Bar dataKey="ok" fill="#16a34a" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="non_conformi" fill="#dc2626" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* pie distribuzione */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Distribuzione esiti</h3>
            {kpiLoading ? <ChartSkeleton /> : (
              pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                      dataKey="value" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                      labelLine={false} fontSize={11}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <PieTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-60 flex items-center justify-center text-gray-400 text-sm">
                  Nessun dato nel periodo
                </div>
              )
            )}
          </div>

          {/* causali anomalia */}
          {kpi && kpi.anomalie_per_causale.length > 0 && (
            <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Causali anomalia</h3>
              <div className="flex flex-wrap gap-3">
                {kpi.anomalie_per_causale.map(({ causale, count }) => (
                  <div key={causale} className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-4 py-2">
                    <span className="text-sm font-medium text-red-700">{CAUSALE_LABEL[causale] ?? causale}</span>
                    <span className="text-lg font-bold text-red-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* tabella non-conformi */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">
              Dettaglio non conformità
              {ncData && <span className="ml-2 text-gray-400 font-normal">({ncData.items.length})</span>}
            </h3>
          </div>
          {ncLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Caricamento…</div>
          ) : !ncData?.items.length ? (
            <div className="p-8 text-center text-gray-400 text-sm">Nessuna non conformità nel periodo selezionato.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3 text-left">Data/ora</th>
                    <th className="px-4 py-3 text-left">Cod. TARI</th>
                    <th className="px-4 py-3 text-left">Intestatario</th>
                    <th className="px-4 py-3 text-left">Indirizzo</th>
                    <th className="px-4 py-3 text-left">Causale</th>
                    <th className="px-4 py-3 text-left">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ncData.items.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtTs(r.timestamp)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">{r.codice_tari ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{r.intestatario ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.indirizzo ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                          r.causale === "BIDONE_NON_ESPOSTO"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {CAUSALE_LABEL[r.causale ?? ""] ?? r.causale ?? "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{r.nota ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Esposizioni per utenza */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Esposizioni per Utenza</h3>
              <p className="text-xs text-gray-400 mt-0.5">Per ogni utenza e contenitore: esposizioni OK, non esposte, conferite con errore nel periodo selezionato</p>
            </div>
            <button
              onClick={handleExportEsposizioni}
              disabled={exportingEsp}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              {exportingEsp ? (
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              )}
              Scarica CSV
            </button>
          </div>
          <div className="px-5 py-3 text-xs text-gray-500">
            Periodo: <strong>{fmt(dataDa)}</strong> – <strong>{fmt(dataA)}</strong> — Colonne: codice TARI, intestatario, indirizzo, CAP, tipo, barcode, frazione, litri, esposizioni OK, non esposte, non conformi, totale
          </div>
        </div>

      </div>
    </div>
  );
}

// ── sotto-componenti ──────────────────────────────────────────────────────────

function KpiCard({ label, value, loading, color, sub }: {
  label: string; value?: number; loading: boolean; color: "blue" | "green" | "red" | "amber"; sub?: string;
}) {
  const colors = {
    blue:  { bg: "bg-blue-50",  text: "text-blue-700",  num: "text-blue-900"  },
    green: { bg: "bg-green-50", text: "text-green-700", num: "text-green-900" },
    red:   { bg: "bg-red-50",   text: "text-red-700",   num: "text-red-900"   },
    amber: { bg: "bg-amber-50", text: "text-amber-700", num: "text-amber-900" },
  }[color];

  return (
    <div className={`${colors.bg} rounded-xl p-5 border border-gray-100`}>
      <p className={`text-xs font-semibold ${colors.text} uppercase tracking-wide mb-1`}>{label}</p>
      {loading ? (
        <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mt-1" />
      ) : (
        <p className={`text-3xl font-black ${colors.num}`}>{value ?? 0}</p>
      )}
      {sub && <p className={`text-sm font-semibold ${colors.text} mt-1`}>{sub}</p>}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="h-60 flex items-end gap-2 px-2">
      {[60, 90, 40, 110, 75, 95, 55, 80, 100, 65].map((h, i) => (
        <div key={i} className="flex-1 bg-gray-100 rounded animate-pulse" style={{ height: `${h}%` }} />
      ))}
    </div>
  );
}
